import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../ui/sheet';
import { Luggage, Briefcase, Armchair, Plus, Minus, Check, Info, X } from 'lucide-react';
import { DuffelSeatMap } from './DuffelSeatMap';
import { money, paxLabel, sliceLabel, SliceHeader, extrasTotals } from './duffelShared';

const Stepper = ({ value, max, onChange, testId }) => (
  <div className="flex items-center gap-2">
    <Button type="button" size="icon" variant="outline" className="h-8 w-8 rounded-full" disabled={value <= 0} onClick={() => onChange(value - 1)} data-testid={`${testId}-minus`}><Minus className="h-4 w-4" /></Button>
    <span className="w-6 text-center font-bold" data-testid={`${testId}-qty`}>{value}</span>
    <Button type="button" size="icon" variant="outline" className="h-8 w-8 rounded-full" disabled={value >= max} onClick={() => onChange(value + 1)} data-testid={`${testId}-plus`}><Plus className="h-4 w-4" /></Button>
  </div>
);

const includedText = (extras, paxId, segIds) => {
  const bags = segIds.map((s) => extras.included_baggage?.[paxId]?.[s]).filter(Boolean);
  const carry = bags.length ? Math.min(...bags.map((b) => b.carry_on)) : 0;
  const checked = bags.length ? Math.min(...bags.map((b) => b.checked)) : 0;
  return `${carry ? `${carry} cabin bag` : 'No cabin bag'} · ${checked ? `${checked} checked bag${checked > 1 ? 's' : ''}` : 'no checked bag'} included in fare`;
};

const BaggageDrawer = ({ open, onClose, extras, slices, sel, setSel }) => {
  const groups = [
    ...(extras.baggage.some((b) => b.scope === 'trip') ? [{ key: 'trip', title: 'Whole trip (all flights)', bags: extras.baggage.filter((b) => b.scope === 'trip'), segIds: extras.segments.map((s) => s.id) }] : []),
    ...slices.map((sl) => ({ key: `s${sl.index}`, title: `${sliceLabel(sl.index)} · ${sl.origin} → ${sl.destination}`, bags: extras.baggage.filter((b) => b.scope === 'slice' && b.slice_indexes[0] === sl.index), segIds: extras.segments.filter((s) => s.slice_index === sl.index).map((s) => s.id) })),
  ].filter((g) => g.bags.length);
  const setQty = (id, qty) => setSel((p) => ({ ...p, bags: { ...p.bags, [id]: qty } }));
  const totals = extrasTotals(extras, sel);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="baggage-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Luggage className="h-5 w-5 text-[#FF6600]" />Extra baggage</SheetTitle>
          <SheetDescription>Bags already included in your fare are shown first. Add checked bags per passenger below.</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 py-6">
          {groups.length === 0 && <p className="text-sm text-slate-500" data-testid="baggage-none">The airline does not offer additional bags on this fare.</p>}
          {groups.map((g) => (
            <div key={g.key} className="rounded-xl border border-slate-200 p-4" data-testid={`baggage-group-${g.key}`}>
              <div className="font-bold text-slate-900 mb-3">{g.title}</div>
              {extras.passengers.filter((p) => p.type !== 'infant_without_seat').map((p) => {
                const options = g.bags.filter((b) => b.passenger_ids.includes(p.id));
                return (
                  <div key={p.id} className="border-t border-slate-100 pt-3 mt-3 first:border-0 first:pt-0 first:mt-0">
                    <div className="font-semibold text-slate-800">{paxLabel(p, extras.passengers)}</div>
                    <div className="flex items-center gap-2 text-xs text-emerald-700 mt-1"><Briefcase className="h-3.5 w-3.5" />{includedText(extras, p.id, g.segIds)}</div>
                    {options.length === 0 ? (
                      <p className="text-sm text-slate-500 mt-2">No additional bags offered by the airline for this passenger.</p>
                    ) : options.map((b) => (
                      <div key={b.id} className="flex items-center justify-between gap-3 mt-3 rounded-lg bg-slate-50 px-3 py-2" data-testid={`bag-option-${b.id}`}>
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{b.weight_kg ? `${b.weight_kg}kg ` : ''}{b.bag_type === 'carry_on' ? 'cabin' : 'checked'} bag</div>
                          <div className="text-xs text-slate-500">{money(b.amount, 'GBP')} each · max {b.max_quantity}</div>
                        </div>
                        <Stepper value={sel.bags[b.id] || 0} max={b.max_quantity} onChange={(q) => setQty(b.id, q)} testId={`bag-${b.id}`} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <SheetFooter className="sticky -bottom-6 -mx-6 px-6 pb-6 bg-white border-t pt-4 flex-row items-center justify-between sm:justify-between">
          <div className="text-sm text-slate-600">Bags total: <strong className="text-slate-900" data-testid="baggage-drawer-total">{money(totals.bags)}</strong></div>
          <Button onClick={onClose} className="bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold" data-testid="baggage-drawer-done"><Check className="h-4 w-4 mr-2" />Done</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

const SeatDrawer = ({ open, onClose, extras, slices, sel, setSel, initialSegment }) => {
  const seatPax = extras.passengers.filter((p) => p.type !== 'infant_without_seat');
  const [segId, setSegId] = useState(initialSegment || extras.segments[0]?.id);
  const [paxId, setPaxId] = useState(seatPax[0]?.id);
  const map = extras.seat_maps.find((m) => m.segment_id === segId);
  const seg = extras.segments.find((s) => s.id === segId);
  const chosen = sel.seats[segId] || {};
  const selectedBySeat = Object.fromEntries(Object.entries(chosen).filter(([, s]) => s).map(([pid, s]) => [s.designator, { passengerId: pid }]));

  const pick = (service, el) => {
    setSel((p) => {
      const cur = p.seats[segId]?.[paxId];
      const next = cur?.designator === el.designator ? null : { id: service.id, designator: el.designator, amount: service.amount, name: el.name };
      return { ...p, seats: { ...p.seats, [segId]: { ...(p.seats[segId] || {}), [paxId]: next } } };
    });
    if (!(sel.seats[segId]?.[paxId]?.designator === el.designator)) {
      const idx = seatPax.findIndex((x) => x.id === paxId);
      if (idx < seatPax.length - 1) setPaxId(seatPax[idx + 1].id);
    }
  };
  const clear = (pid) => setSel((p) => ({ ...p, seats: { ...p.seats, [segId]: { ...(p.seats[segId] || {}), [pid]: null } } }));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="seat-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Armchair className="h-5 w-5 text-[#FF6600]" />Choose your seats</SheetTitle>
          <SheetDescription>Pick a flight, then a passenger, then tap a seat. Prices are per seat, per flight.</SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap gap-2 mt-5">
          {extras.segments.map((s) => (
            <button key={s.id} type="button" onClick={() => setSegId(s.id)} data-testid={`seat-segment-tab-${s.slice_index}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${s.id === segId ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}>
              {sliceLabel(s.slice_index)} · {s.origin}→{s.destination} · {s.flight_number}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {seatPax.map((p, i) => (
            <button key={p.id} type="button" onClick={() => setPaxId(p.id)} data-testid={`seat-passenger-tab-${i}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${p.id === paxId ? 'bg-orange-50 border-[#FF6600] text-[#FF6600] font-bold' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}>
              {paxLabel(p, extras.passengers)}{chosen[p.id] ? ` · ${chosen[p.id].designator}` : ''}
            </button>
          ))}
        </div>

        <div className="py-6">
          {!map || !map.available ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="seat-map-unavailable">
              <Armchair className="h-8 w-8 text-slate-300 mx-auto mb-3" />
              <p className="font-semibold text-slate-800">Seat selection isn't available for {seg ? `${seg.origin} → ${seg.destination}` : 'this flight'}</p>
              <p className="text-sm text-slate-500 mt-1">The airline will allocate seats free of charge at check-in. You can still choose seats on your other flights.</p>
            </div>
          ) : (
            <DuffelSeatMap map={map} passengerId={paxId} passengers={seatPax} selectedBySeat={selectedBySeat} onPick={pick} />
          )}
        </div>

        <SheetFooter className="sticky -bottom-6 -mx-6 px-6 pb-6 bg-white border-t pt-4 flex-col sm:flex-col gap-3">
          <div className="flex flex-wrap gap-2 w-full">
            {seatPax.map((p) => chosen[p.id] ? (
              <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-orange-50 border border-orange-200 px-3 py-1 text-xs font-semibold text-slate-800" data-testid={`seat-chip-${p.id}`}>
                {paxLabel(p, extras.passengers)}: {chosen[p.id].designator} · {money(chosen[p.id].amount)}
                <button type="button" onClick={() => clear(p.id)} className="ml-1 text-slate-400 hover:text-red-500" aria-label="Remove seat"><X className="h-3 w-3" /></button>
              </span>
            ) : null)}
          </div>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-slate-600">Seats total: <strong className="text-slate-900" data-testid="seat-drawer-total">{money(extrasTotals(extras, sel).seats)}</strong></div>
            <Button onClick={onClose} className="bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold" data-testid="seat-drawer-done"><Check className="h-4 w-4 mr-2" />Done</Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export const DuffelExtras = ({ extras, slices, sel, setSel }) => {
  const [drawer, setDrawer] = useState(null); // 'bags' | {seat: segmentId}
  const totals = extrasTotals(extras, sel);
  const bagCount = Object.values(sel.bags).reduce((a, q) => a + q, 0);
  const seatPax = extras.passengers.filter((p) => p.type !== 'infant_without_seat');
  const anySeatMap = extras.seat_maps.some((m) => m.available);

  return (
    <div className="space-y-5" data-testid="duffel-extras-step">
      <Card>
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-orange-50 flex items-center justify-center"><Luggage className="h-6 w-6 text-[#FF6600]" /></div>
            <div>
              <div className="font-bold text-slate-900">Extra baggage</div>
              <div className="text-sm text-slate-600">{bagCount ? `${bagCount} extra bag${bagCount > 1 ? 's' : ''} added · ${money(totals.bags)}` : extras.baggage.length ? `Checked bags from ${money(Math.min(...extras.baggage.map((b) => b.amount)))}` : 'No extra bags offered on this fare'}</div>
            </div>
          </div>
          <Button variant="outline" disabled={!extras.baggage.length} onClick={() => setDrawer('bags')} className="border-[#FF6600] text-[#FF6600] hover:bg-orange-50 font-semibold" data-testid="open-baggage-drawer">{bagCount ? 'Edit bags' : 'Add bags'}</Button>
        </CardContent>
      </Card>

      {slices.map((sl) => {
        const segs = extras.segments.filter((s) => s.slice_index === sl.index);
        return (
          <Card key={sl.index} data-testid={`extras-slice-${sl.index}`}>
            <CardContent className="p-5">
              <SliceHeader slice={sl} label={sliceLabel(sl.index)} />
              {segs.map((seg) => {
                const map = extras.seat_maps.find((m) => m.segment_id === seg.id);
                const chosen = sel.seats[seg.id] || {};
                const picked = seatPax.filter((p) => chosen[p.id]);
                return (
                  <div key={seg.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3 mt-2" data-testid={`seat-row-${seg.id}`}>
                    <div className="flex items-start gap-3">
                      <Armchair className="h-5 w-5 text-[#FF6600] mt-0.5" />
                      <div>
                        <div className="font-semibold text-slate-900">Seats · {seg.origin} → {seg.destination} <span className="text-slate-500 font-normal">({seg.flight_number})</span></div>
                        {map?.available ? (
                          <div className="text-sm text-slate-600">{picked.length ? picked.map((p) => `${paxLabel(p, extras.passengers)} ${chosen[p.id].designator}`).join(' · ') : `${seatPax.length} passenger${seatPax.length > 1 ? 's' : ''} · seats not yet chosen`}</div>
                        ) : (
                          <div className="text-sm text-slate-500 flex items-center gap-1" data-testid={`seat-unavailable-${seg.id}`}><Info className="h-3.5 w-3.5" />Seat selection not available — allocated free at check-in</div>
                        )}
                      </div>
                    </div>
                    {map?.available && <Button variant="outline" onClick={() => setDrawer({ seat: seg.id })} className="border-[#FF6600] text-[#FF6600] hover:bg-orange-50 font-semibold" data-testid={`open-seat-drawer-${seg.id}`}>{picked.length ? 'Change seats' : 'Choose seats'}</Button>}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
      {!anySeatMap && <p className="text-xs text-slate-500">Seat maps are not provided by the airline for these flights.</p>}

      {drawer === 'bags' && <BaggageDrawer open onClose={() => setDrawer(null)} extras={extras} slices={slices} sel={sel} setSel={setSel} />}
      {drawer?.seat && <SeatDrawer open onClose={() => setDrawer(null)} extras={extras} slices={slices} sel={sel} setSel={setSel} initialSegment={drawer.seat} />}
    </div>
  );
};
